import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { view } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import getColumnsAmountOptionByValue from '../helpers/get-columns-amount-option-by-value';
import controlIds from '../constants/control-ids';
import columnsAmountOptions, {
  columnsAmountValueByOption,
  columnsAmountValuesByOption,
} from '../constants/columns-amount-options';
import SwitchControl from './switch-control';

const { TabSelector, HorizontalStack } = designSystem;
const {
  Context: ElementSettingsContext,
  constants: { targetClasses },
} = elementSettings;

const tabs = [
  { id: columnsAmountOptions.ONE_COLUMN, label: '1' },
  { id: columnsAmountOptions.TWO_COLUMNS, label: '2' },
  { id: columnsAmountOptions.THREE_COLUMNS, label: '3' },
  { id: columnsAmountOptions.FOUR_COLUMNS, label: '4' },
  { id: columnsAmountOptions.FIVE_COLUMNS, label: '5', disabled: true },
  { id: columnsAmountOptions.SIX_COLUMNS, label: '6' },
];

const getBatchToSetColWidth = (columnEntries, value) =>
  columnEntries.reduce(
    (batch, { elementId }) => ({
      ...batch,
      [elementId]: {
        [targetClasses.COL]: value,
      },
    }),
    {}
  );

function AmountControl({ elementId, noWrapControl = null, onChange, onClick, onNoWrapChange }) {
  const { deviceMode, resolvedState } = useContext(ElementSettingsContext);
  const isBreakpointEditingEnabled = deviceMode?.active;
  const columns = view.accessors.getLiveElement(elementId);
  const columnElements = columns.querySelectorAll(':scope > ws-column');
  const columnEntries = Array.from(columnElements).map((element) => ({
    element,
    elementId: view.accessors.getLiveElementId(element),
  }));
  const currentColValues = columnEntries.map(
    ({ elementId: columnElementId }) => resolvedState[columnElementId]?.[targetClasses.COL]
  );
  const hasCustomColumnWidth = currentColValues.some(
    (value) => Boolean(value) && !getColumnsAmountOptionByValue(value)
  );
  const selected = !hasCustomColumnWidth ? getColumnsAmountOptionByValue(currentColValues[0]) : '';

  const handleChange = useCallback(
    (columnsAmountOption) => {
      if (isBreakpointEditingEnabled) {
        const colValue = columnsAmountValueByOption[columnsAmountOption];

        if (colValue) {
          onChange(getBatchToSetColWidth(columnEntries, colValue));
        }
      } else {
        const colValues = columnsAmountValuesByOption[columnsAmountOption];

        if (colValues) {
          onChange(getBatchToSetColWidth(columnEntries, colValues));
        }
      }

      onClick({ controlId: controlIds.COLUMNS_AMOUNT });
    },
    [columnEntries, isBreakpointEditingEnabled, onChange, onClick]
  );

  return (
    <HorizontalStack verticalAlign="center" align="space-between" gap="spacing-3-xs">
      <TabSelector size="small" activeTabId={selected} tabs={tabs} onClick={handleChange} />
      {noWrapControl && <SwitchControl {...noWrapControl} inverted onChange={onNoWrapChange} />}
    </HorizontalStack>
  );
}

AmountControl.propTypes = {
  elementId: PropTypes.string.isRequired,
  noWrapControl: PropTypes.shape({
    id: PropTypes.string.isRequired,
    elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    targets: PropTypes.arrayOf(PropTypes.string).isRequired,
    glyph: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onNoWrapChange: PropTypes.func.isRequired,
};

export default AmountControl;
